'use client';

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { parseStat, formatStat, easeOutCubic } from '@/lib/count-up';

export interface CountUpProps {
  /** The stat exactly as authored, e.g. "1,000+" — also what the server renders. */
  text: string;
  className?: string;
  style?: CSSProperties;
  durationMs?: number;
}

/** Start counting once this much of the element is visible. */
export const COUNT_UP_THRESHOLD = 0.3;

/**
 * Counts a stat up from 0 the first time it scrolls into view, then lands on
 * the original text verbatim. The server renders the final value (crawlers
 * and no-JS see the real number); animation starts only after hydration and
 * only when the reader has not asked for reduced motion. Runs once.
 */
export default function CountUp({ text, className, style, durationMs = 1600 }: CountUpProps) {
  const [display, setDisplay] = useState(text);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = ref.current;
    const parsed = parseStat(text);
    if (!el || !parsed) return;
    if (typeof IntersectionObserver === 'undefined') return;
    if (typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!(durationMs > 0)) return; // nothing sensible to animate; the final value is already shown

    let frame = 0;
    let start = -1;
    let started = false;

    const tick = (now: number) => {
      if (start < 0) start = now;
      const progress = Math.min(1, (now - start) / durationMs);
      if (progress >= 1) {
        setDisplay(text); // the authored string, never a reformatted final
        return;
      }
      setDisplay(formatStat(parsed, parsed.value * easeOutCubic(progress)));
      frame = requestAnimationFrame(tick);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || started) return;
        started = true;
        observer.disconnect();
        setDisplay(formatStat(parsed, 0));
        frame = requestAnimationFrame(tick);
      },
      { threshold: COUNT_UP_THRESHOLD },
    );
    observer.observe(el);

    return () => {
      observer.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [text, durationMs]);

  const counting = display !== text;
  return (
    <span ref={ref} className={className} style={style}>
      {counting ? (
        <>
          <span className="sr-only">{text}</span>
          <span aria-hidden="true">{display}</span>
        </>
      ) : (
        text
      )}
    </span>
  );
}
